/**
 * ShellHeader - the header bar, with the character library it opens and the tracker switch.
 *
 * THE CHOSEN FOLDER OUTLIVES THE LIBRARY PANEL (v8.24.0): it closes on every
 * outside click, and the File objects stay good for the life of the page -
 * so the folder is chosen once a visit, and held by the shell. Switching the
 * tracker OFF asks whether to keep its state for later (cancel does
 * nothing). Extracted from DeathWatchSimulator; the markup is unchanged.
 */
import { useMemo } from 'react';
import Header from '../Header.jsx';
import { teamsIn } from '../../engine/roster/team';
import CharacterLibrary from '../CharacterLibrary';

/** The tracker switch: on at once; off only after asking what to keep. */
export async function toggleTracker(turnTracker, showDialogAsync) {
  if (!turnTracker.trackerState.active) {
    turnTracker.toggleTracker(true);
    return;
  }
  const keep = await showDialogAsync('confirm', 'Keep tracker state for later?');
  if (keep === null) return; // cancelled
  turnTracker.toggleTracker(false, !!keep);
}

/** The library panel, opening the editor on a character or a new one. */
function LibraryNode({ p }) {
  const { canSeeRooms, setShowCharacterLibrary } = p;
  const openEditor = (character) => {
    p.setEditingCharacter(character);
    p.setShowCharacterEditor(true);
    setShowCharacterLibrary(false);
  };
  return (
    <CharacterLibrary
      library={p.activeLibrary}
      onEdit={(c) => openEditor(c)}
      onNew={() => openEditor(null)}
      onLoadCharacter={p.handleLoadCharacter}
      onSendToRoom={canSeeRooms ? p.sendToRoom : undefined}
      onLoadMany={p.loadCharacters}
      folderFiles={p.folderFiles}
      onFolderFiles={p.setFolderFiles}
      waitingRooms={canSeeRooms ? p.waiting.rooms.map((r) => ({ id: r.id, name: r.name })) : []}
      isOpen={p.showCharacterLibrary}
      onClose={() => setShowCharacterLibrary(false)}
      tableAssets={p.tableAssets}
    />
  );
}

/** @param {object} p - everything the header, the library and the switch read (see the call site). */
export default function ShellHeader(p) {
  const { sessionId, isGm, turnTracker, handoutsDrawer } = p;
  // Memoised: the header is `memo`, and a fresh list every render would defeat it.
  const teams = useMemo(() => teamsIn(p.roster), [p.roster]);
  return (
    <Header
      sessionId={sessionId}
      isGm={isGm}
      session={p.session}
      user={p.user}
      onlinePlayers={p.onlinePlayers}
      connectedPlayers={p.connectedPlayers}
      direction={p.direction}
      danger={p.danger}
      teams={teams}
      fightSides={p.fightSides}
      onStartFight={p.startFight}
      canUndo={p.canUndo}
      undoSaving={p.undoSaving}
      isLocked={p.isLocked}
      doUndo={p.doUndo}
      doExport={p.doExport}
      doImport={p.doImport}
      fileInputRef={p.fileInputRef}
      setShowHotkeys={p.setShowHotkeys}
      setShowPermPanel={p.setShowPermPanel}
      setShowSettings={p.setShowSettings}
      canEditSession={!!sessionId && isGm}
      setSession={p.setSession}
      onLeave={p.onLeave}
      showCharacterLibrary={p.showCharacterLibrary}
      setShowCharacterLibrary={p.setShowCharacterLibrary}
      characterLibraryNode={<LibraryNode p={p} />}
      turnTrackerActive={turnTracker.trackerState.active}
      onToggleTurnTracker={() => toggleTracker(turnTracker, p.showDialogAsync)}
      turnTrackerLocked={p.isLocked('turnTracker')}
      onToggleHandouts={p.handoutsContextValue ? handoutsDrawer.toggleCollapsed : null}
      handoutsOpen={!handoutsDrawer.collapsed}
      handoutCount={p.handoutCount}
      handoutUnread={p.handoutUnread}
      assetsHref={isGm && sessionId && p.user?.canHost !== false ? `?assets&session=${sessionId}` : null}
    />
  );
}
